import { normalizeHostInfo } from "./normalizeHostInfo";
import { HostInfo } from "./types";

/**
 * Lê os dados do host (modelo, sistema operacional, versão do host e metadados).
 *
 * Ordem:
 *   1. `window.__MEEP_HOST__` — injetado pelo app Android antes do bundle (síncrono)
 *   2. Android: `RNBridge.callNative("getHostInfo")`; app antigo sem esse handler
 *      cai em `getDeviceInfo` (só serial e versão)
 *   3. Windows: `appConfigurationService.get().HostInfo`; casca antiga sem o campo
 *      usa `MacAddress` e `VersaoCefSharp`
 *   4. nada disso: host "unknown"
 *
 * Nunca rejeita: em host antigo ou com erro devolve o que conseguiu montar.
 */
const BRIDGE_TIMEOUT_MS = 1500;
const HOST_OBJECT_NAME = "appConfigurationService";

type AnyWindow = Window & Record<string, any>;

const win = (): AnyWindow | undefined => (typeof window !== "undefined" ? (window as unknown as AnyWindow) : undefined);

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timeout ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

const userAgent = (): string => (typeof navigator !== "undefined" ? navigator.userAgent || "" : "");

/** "Mozilla/5.0 (Linux; Android 11; M11 Pro Build/RP1A...; wv)" -> { version: "11", model: "M11 Pro" } */
export const parseAndroidUserAgent = (ua: string): { version: string; model: string } => {
  const match = /Android\s+([\d.]+)(?:;\s*([^;)]+?))?(?:\s+Build\/|;|\))/i.exec(ua);
  if (!match) return { version: "", model: "" };
  const model = (match[2] || "").trim();
  return { version: match[1], model: /^(wv|K|Mobile)$/i.test(model) ? "" : model };
};

/** "Windows NT 10.0" -> "10.0" (Win 10 e 11 informam o mesmo valor no userAgent). */
export const parseWindowsUserAgent = (ua: string): string => {
  const match = /Windows NT\s+([\d.]+)/i.exec(ua);
  return match ? match[1] : "";
};

const fromAndroidLegacy = (deviceInfo: any): HostInfo => {
  const ua = parseAndroidUserAgent(userAgent());
  return normalizeHostInfo(
    {
      platform: "android",
      host: { type: "rn", version: deviceInfo?.appVersion },
      os: { name: "Android", version: ua.version },
      device: { model: ua.model, serial: deviceInfo?.serialNumber },
    },
    "legacy-bridge"
  );
};

const readAndroid = async (bridge: any): Promise<HostInfo> => {
  try {
    const info = await withTimeout<any>(bridge.callNative("getHostInfo"), BRIDGE_TIMEOUT_MS);
    if (info && typeof info === "object") return normalizeHostInfo(info, "bridge");
  } catch (error) {
    // app Android antigo: "Native handler not found: getHostInfo"
  }
  try {
    const deviceInfo = await withTimeout<any>(bridge.callNative("getDeviceInfo"), BRIDGE_TIMEOUT_MS);
    return fromAndroidLegacy(deviceInfo);
  } catch (error) {
    return fromAndroidLegacy(undefined);
  }
};

const readCefSharp = async (w: AnyWindow): Promise<HostInfo> => {
  let config: any;
  try {
    await withTimeout(Promise.resolve(w.CefSharp.BindObjectAsync(HOST_OBJECT_NAME)), BRIDGE_TIMEOUT_MS);
    const service = w[HOST_OBJECT_NAME];
    if (service && typeof service.get === "function") {
      config = await withTimeout<any>(service.get(), BRIDGE_TIMEOUT_MS);
    }
  } catch (error) {
    config = undefined;
  }

  const hostInfo = config?.HostInfo ?? config?.hostInfo;
  if (hostInfo) {
    const info = normalizeHostInfo(hostInfo, "cefsharp");
    if (info.platform !== "unknown") return info;
  }

  // Casca antiga: só MAC e versão.
  return normalizeHostInfo(
    {
      platform: "windows",
      host: { type: "cefsharp", version: config?.VersaoCefSharp },
      os: { name: "Windows", version: parseWindowsUserAgent(userAgent()) },
      device: { family: "pc", macAddress: config?.MacAddress },
    },
    "cefsharp-legacy"
  );
};

const readHostInfo = async (): Promise<HostInfo> => {
  const w = win();
  if (!w) return normalizeHostInfo({}, "none");

  if (w.__MEEP_HOST__) {
    const injected = normalizeHostInfo(w.__MEEP_HOST__, "injected");
    if (injected.platform !== "unknown") return injected;
  }
  if (w.RNBridge && typeof w.RNBridge.callNative === "function") {
    return readAndroid(w.RNBridge);
  }
  if (w.CefSharp && typeof w.CefSharp.BindObjectAsync === "function") {
    return readCefSharp(w);
  }
  return normalizeHostInfo({}, "none");
};

let cached: HostInfo | null = null;
let pending: Promise<HostInfo> | null = null;

export const getHostInfo = (): Promise<HostInfo> => {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = readHostInfo()
      .catch(() => normalizeHostInfo({}, "none"))
      .then((info) => {
        cached = info;
        const w = win();
        // Global de diagnóstico: `window.meepHost` no console do host.
        if (w) w.meepHost = info;
        return info;
      });
  }
  return pending;
};

/** HostInfo já carregado, ou `null` se `getHostInfo` ainda não terminou. */
export const getHostInfoSync = (): HostInfo | null => cached;

/** Só para testes. */
export const resetHostInfoCache = (): void => {
  cached = null;
  pending = null;
};
